import { AsoEbiStatus, AsoEbiTransactionCostBearer } from "./aso.ebi";
import { MailType } from "./mailcredit";

export enum UserWeddingRole {
  Bride = "bride",
  Groom = "groom",
  EventPlanner = "eventplanner",
  GuestCheckIn = "guestcheckin",
}
export type CreatorContext = "user" | "event_planner";

export type WeddingMainContent = {
  date: Date;
  hashtag: string;
  logo: string;
  shouldDisplayLogo: boolean;
  coupleLoveStory: string;
  coupleImage: string;
  useHashtagAsHeaderText: boolean;
};

export type BrideAndGroomShorts = {
  brideShortWords: string;
  brideImage: string;
  groomShortWords: string;
  groomImage: string;
};

export type EventColor = {
  colorName: string;
  colorCode: string;
  colorPaletteURL?: string;
};

export type EventColors = {
  color1: EventColor;
  color2: EventColor;
};
export type DressCode = {
  bride: string;
  groom: string;
};
export type EventSchedule = {
  _id: string;
  eventType: string;
  address: string;
  date: Date; // local date
  rawDate: {
    minutes: number;
    hours: number;
    day: number;
    month: number;
    year: number;
  };
  timezone: { name: string; offset: string };
  note: string;
  colors: EventColors;
  dressCode: DressCode;
  hideAddress: boolean;
  hideFromRSVP: boolean;
};

export type GallerySection = {
  sectionTitle: string;
  images: string[];
};

export type CountdownSection = {
  fianceeName: string;
  fianceeEmail: string;
  weddingDate: string;
  image: string;
};

export type BankAccount = {
  country: string;
  bankName: string;
  accountNumber: string;
  accountName: string;
  currency: string;
  sortCode: string;
};

export type PaymentLink = {
  title: string;
  url: string;
};

export type wishlistLink = {
  title: string;
  url: string;
};

export type FAQ = {
  question: string;
  answer: string;
};

export type Template = "a" | "b";

export enum GuestLinkStatus {
  Activated = "activated",
  Deactivated = "deactivated",
}

export enum GuestRestrictRSVPStatus {
  Activated = "activated",
  Deactivated = "deactivated",
}

export enum ResolveRestrictedRSVPWithEmail {
  Activated = "activated",
  Deactivated = "deactivated",
}

export enum GetPhoneWithRSVPStatus {
  Activated = "activated",
  Deactivated = "deactivated",
}

export enum GetEmailWithRSVPStatus {
  Activated = "activated",
  Deactivated = "deactivated",
}

export enum LoveWallStatus {
  Activated = "activated",
  Deactivated = "deactivated",
}

export enum LoveWallRestrictionStatus {
  Active = "active",
  Inactive = "inactive",
}

export enum Meridian {
  am = "am",
  pm = "pm",
}

export type Time = {
  hours: number;
  minutes: number;
  meridian: Meridian;
};

export type Program = {
  _id: string;
  time: Time;
  title: string;
};

export type Menu = {
  _id: string;
  category: string;
  dishes: string[];
};

export type MailRemindersMap = Record<MailType, string>;

export type PreferenceTabs = "access" | "form";

export enum SendRSVPEmailStatus {
  Immediately = "immediately",
  Later = "later",
}

export const defaultHeroSectionOrder = [
  "hero_main_text",
  "short_description",
  "wedding_date",
  "rsvp_button",
] as Array<HeroSectionItems>;

export type HeroSectionItems =
  "hero_main_text" | "short_description" | "wedding_date" | "rsvp_button";

export type DisplayStatus = "shown" | "hidden";

export type FormFieldType = "input" | "select" | "multiSelect";

export type ButtonShape = "rounded" | "soft" | "straight" | "pill";
export type ButtonBorder = "filled" | "outline";
export type ButtonStyle = {
  buttonShape: ButtonShape;
  buttonBorder: ButtonBorder;
};

export type ValueSet = {
  isDefault: boolean;
  text: string;
};

export const defaultGuestFormFieldIDs = [
  "x__default__config__firstname",
  "x__default__config__lastname",
  "x__default__config__email",
  "x__default__config__phone",
  "x__default__config__relationship",
  "x__default__config__address",
  "x__default__config__events",
  "x__default__config__coming_for",
];

// Updated default configuration
export const createDefaultGuestFormConfiguration =
  (): GuestFormConfiguration[] => [
    {
      id: "x__default__config__firstname",
      label: "First Name",
      placeholder: "E.g Matt",
      required: true,
      isChecked: true,
      displayStatus: "shown",
      removable: false,
      formFieldType: "input",
    },
    {
      id: "x__default__config__lastname",
      label: "Last Name",
      placeholder: "E.g Damon",
      required: true,
      isChecked: true,
      displayStatus: "shown",
      removable: false,
      formFieldType: "input",
    },
    {
      id: "x__default__config__email",
      label: "Email Address",
      placeholder: "yourname@gmail.com",
      required: false,
      isChecked: true,
      displayStatus: "shown",
      removable: false,
      formFieldType: "input",
    },
    {
      id: "x__default__config__phone",
      label: "Phone number",
      placeholder: "+234 812 345 6789",
      required: false,
      isChecked: true,
      displayStatus: "shown",
      removable: false,
      formFieldType: "input",
    },
    {
      id: "x__default__config__relationship",
      label: "Relationship",
      placeholder: "Colleague at work",
      required: false,
      isChecked: true,
      displayStatus: "shown",
      removable: false,
      formFieldType: "input",
    },
    {
      id: "x__default__config__address",
      label: "Address",
      placeholder: "Address (optional), for delivery of access cards and more",
      required: false,
      isChecked: true,
      displayStatus: "shown",
      removable: false,
      formFieldType: "input",
    },
    {
      id: "x__default__config__events",
      label: "Events",
      placeholder: "",
      required: true,
      isChecked: true,
      displayStatus: "shown",
      removable: false,
      formFieldType: "multiSelect",
      options: [],
    },
    {
      id: "x__default__config__coming_for",
      label: "Coming for?",
      placeholder: "Select who you are coming for",
      required: false,
      isChecked: true,
      displayStatus: "shown",
      removable: false,
      formFieldType: "select",
      options: [
        {
          optionId: "bride",
          optionText: "Bride",
        },
        {
          optionId: "groom",
          optionText: "Groom",
        },
        {
          optionId: "both",
          optionText: "Bride | Groom",
        },
      ],
    },
  ];

export type GuestFormConfigurationSelectOption = {
  optionId: string;
  optionText: string;
};

export interface GuestFormConfiguration {
  id: string;
  label: string;
  placeholder: string; // New property for placeholder
  required: boolean;
  isChecked: boolean;
  removable: boolean;
  displayStatus: "shown" | "hidden";
  formFieldType: "input" | "select" | "multiSelect"; // New field type
  options?: GuestFormConfigurationSelectOption[]; // Options for 'select' fields
}

export type WeddingPreference = {
  heroSection: {
    order: HeroSectionItems[];
    buttonStyle: ButtonStyle;
    fontFamily: Record<HeroSectionItems, string>;
    values: Record<HeroSectionItems, ValueSet>;
    displayStatus: Record<HeroSectionItems, boolean>;
    asoEbiDisplayStatus: boolean;
    giftingDisplayStatus: boolean;
  };
  guestFormConfiguration: GuestFormConfiguration[];
  pastGuestFormConfiguration: GuestFormConfiguration[];
};

export type WeddingDatePeriod = "ongoing" | "upcoming" | "past";
export type CoupleNameOrder = "bride-first" | "groom-first";

export type Wedding = {
  version: number;
  date: Date;
  faqs: FAQ[];
  _id: string;
  menu: Menu[];
  logo: string;
  ubxdid: string;
  hashtag: string;
  createdAt: Date;
  updatedAt: Date;
  accountID: string;
  template: "a" | "b";
  accessCards: AccessCards;
  coupleNameOrder?: CoupleNameOrder;
  hasSharedWebsite: boolean;
  showHashtagOnDDay: boolean;
  asoEbiStatus: AsoEbiStatus;
  useHashtagAsHeaderText: boolean;
  sendRSVPEmailStatus: SendRSVPEmailStatus;
  weddingPartyOrder: string[];
  gallery: GallerySection[];
  shouldDisplayLogo: boolean;
  coupleLoveStory: string;
  brideShortWords: string;
  groomShortWords: string;
  programme: Program[];
  brideImage: string;
  groomImage: string;
  wishlistURL: string;
  XAccountHash: string;
  giftingMessage: string;
  guestManagers: string[];
  rsvpRelationships: string[];
  bankAccounts: BankAccount[];
  paymentLinks: PaymentLink[];
  personalInformation: string[];
  preference: WeddingPreference;
  loveWallStatus: LoveWallStatus;
  loveWallStandaloneStatus: LoveWallStatus;
  eventSchedules: EventSchedule[];
  guestLinkStatus: GuestLinkStatus;
  maxAdditionalGuestsPerInvite: number;
  clickedDdayGettingStarted: boolean;
  mailRemindersMap: MailRemindersMap;
  getPhoneWithRSVPStatus: GetPhoneWithRSVPStatus;
  getEmailWithRSVPStatus: GetEmailWithRSVPStatus;
  guestRestrictRSVPStatus: GuestRestrictRSVPStatus;
  loveWallRestrictionStatus: LoveWallRestrictionStatus;

  groomID: string;
  brideID: string;
  creatorID: string;
  groomEmail: string;
  brideEmail: string;
  coupleImage: string;
  groomLastName: string;
  brideLastName: string;
  groomJoinedAt: string;
  brideJoinedAt: string;
  groomFirstName: string;
  brideInvitedAt: string;
  brideFirstName: string;
  groomInvitedAt: string;
  creatorRole: UserWeddingRole;
  eventPlannerJoinedAt: string;
  eventPlannerInvitedAt: string;
  // spouseJoinedAt: string;
  // spouseInvitedAt: string;
  //
  //

  welcomeNote: string;
  // creatorGender: WeddingGender;
  // creatorContext: CreatorContext;
  welcomeNoteDisplayStatus: WelcomeNoteDisplayStatus;
  asoEbiTransactionCostBearer: AsoEbiTransactionCostBearer;
  resolveRestrictedRSVPWithEmail: ResolveRestrictedRSVPWithEmail;
};

export type WeddingGender = "bride" | "groom";

export enum WelcomeNoteDisplayStatus {
  WelcomeNoteDisplayStatusShow = "show",
  WelcomeNoteDisplayStatusHide = "hide",
}

export type QRPosition =
  | "top-left"
  | "top-center"
  | "top-right"
  | "middle-left"
  | "middle-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right"
  | "center"
  | "custom";

export type QRCustomPosition = {
  x: number; // percentage from left (0-100)
  y: number; // percentage from top (0-100)
};

export type AccessCardVisibility = {
  showCoupleNames?: boolean;
  showGuestName?: boolean;
  showDate?: boolean;
  showVenue?: boolean;
  showWeddingOfLabel?: boolean;
};

export type AccessCards = {
  address: string;
  hideDetails: boolean;
  showGuestName: boolean;
  showAdmitsOneOnly: boolean;
  showEventName: boolean;
  eventId: string;
  selectedEventType: string;
  date: Date;
  backgroundCtx: "color" | "image";
  backgroundColor: EventColor;
  textColor: EventColor;
  backgroundImage?: string;
  qrPosition?: QRPosition;
  qrCustomPosition?: QRCustomPosition;
  visibility?: AccessCardVisibility;
  overlayOpacity?: number;
  overlayColor?: EventColor;
  qrSize?: number; // QR code size in pixels (default 100, range 60-200)
};
